import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { PlannedEventApi } from "@ramudden/data-access/resource/planned-event-api";
import {
    IPlannedAssignment,
    IPlannedEvent,
    IPlannedVehicle,
    IPlannedWorker,
    PlannedEventCreator,
    PlannedEventUpdater,
} from "@ramudden/models/planned-event";
import { ServiceRequestOptions } from "@ramudden/models/search";
import { firstValueFrom } from "rxjs";
import { AppService } from "../../services/app.service";
import { AuthenticationService } from "../../services/generic/authentication.service";
import { GlobalEventsService } from "../../services/generic/global-events.service";
import { LoaderService } from "../../services/loader.service";
import { CoordinatorChoiceDialogComponent } from "./dialogs/coordinator-choice/coordinator-choice-dialog.component";
import { TeamDialogComponent, TeamDialogOutput } from "./dialogs/team/team-dialog.component";
import { UserDialogComponent, UserDialogType } from "./dialogs/user/user-dialog.component";
import { ISelectedVehicle, VehicleDialogComponent } from "./dialogs/vehicle/vehicle-dialog.component";

@Component({
    selector: "app-setup",
    standalone: true,
    imports: [
        ReactiveFormsModule,
        UserDialogComponent,
        TeamDialogComponent,
        VehicleDialogComponent,
        CoordinatorChoiceDialogComponent,
    ],
    templateUrl: "./setup.component.html",
})
export class SetupComponent implements OnInit {
    @ViewChild("userDialog", { static: true }) userDialog: UserDialogComponent;
    @ViewChild("teamDialog", { static: true }) teamDialog: TeamDialogComponent;
    @ViewChild("vehicleDialog", { static: true }) vehicleDialog: VehicleDialogComponent;
    @ViewChild("coordinatorChoiceDialog", { static: true }) coordinatorChoiceDialog: CoordinatorChoiceDialogComponent;

    private readonly appService = inject(AppService);
    private readonly plannedEventApi = inject(PlannedEventApi);
    private readonly router = inject(Router);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly loaderService = inject(LoaderService);
    private readonly globalEventsService = inject(GlobalEventsService);

    userDialogOutput: UserDialogType;

    splitTeam: boolean;
    disablePreviousTeamDialog = false;
    coordinatorViewMode = false;

    private get isCoordinator(): boolean {
        return this.globalEventsService.isMaasCoordinator();
    }

    ngOnInit() {
        this.appService.restoreState();
        this.disablePreviousTeamDialog = false;

        if (this.appService.config?.isConfigured && !this.appService.coordinatorMode) {
            this.disablePreviousTeamDialog = true;
            this.teamDialog.openModal();
            return;
        }

        // The roles and the logged-in account may not be loaded yet at ngOnInit — wait for them.
        // Wait on the authorization info rather than on the roles: setAuthorizationInfo() applies the roles first and
        // publishes the authorization info afterwards, so the account is the later of the two signals and the
        // coordinator gate needs both.
        const authorizationInfo = this.globalEventsService.getAuthorizationInfo();
        if (authorizationInfo) {
            this.openInitialDialog();
        } else {
            const sub = this.globalEventsService.authorizationInfo$.subscribe((info) => {
                if (info) {
                    sub.unsubscribe();
                    this.openInitialDialog();
                }
            });
        }
    }

    private openInitialDialog() {
        if (this.isCoordinator) {
            this.coordinatorChoiceDialog.openModal();
        } else {
            this.userDialog.openModal();
        }
    }

    onCoordinatorJoinTeam() {
        this.coordinatorViewMode = false;
        this.userDialog.languageOnly = false;
        this.userDialog.teamRequired = true;
        this.userDialog.openModal();
    }

    onCoordinatorViewTeams() {
        this.coordinatorViewMode = true;
        this.userDialog.languageOnly = true;
        this.userDialog.teamRequired = false;
        this.userDialog.openModal();
    }

    onUserDialogNext(userDialogOutput: UserDialogType) {
        this.userDialogOutput = userDialogOutput;

        if (this.coordinatorViewMode) {
            this.appService.coordinatorMode = true;
            this.appService.isCoordinator = true;
            this.appService.selectedWorker = undefined;
            this.appService.config = {
                isConfigured: true,
                language: userDialogOutput.language,
                plannedEventId: undefined,
            };
            this.appService.saveState();
            this.router.navigate(["/team-overview"]);
        } else {
            this.appService.selectedWorker = {
                firstName: userDialogOutput.worker.firstName,
                id: userDialogOutput.worker.id,
                isActive: true,
                isTeamLeader: true,
                lastName: userDialogOutput.worker.lastName,
                organizationId: userDialogOutput.worker.organizationId,
                canDoControl: userDialogOutput.worker.canDoControl,
            };

            this.appService.coordinatorMode = false;
            this.appService.inCoordinatorView = false;
            this.appService.viewingPlannedEvent = undefined;
            this.appService.isCoordinator = this.isCoordinator;
            this.appService.config = {
                language: userDialogOutput.language,
                isConfigured: false,
                plannedEventId: undefined,
            };
            this.appService.plannedEvent = userDialogOutput.plannedEvent;
            this.teamDialog.openModal();
        }
    }

    onTeamDialogPreviousClicked() {
        this.userDialog.openModal(this.userDialogOutput);
    }

    onTeamDialogClosed(teamDialogOutput: TeamDialogOutput) {
        this.appService.selectedWorkers = teamDialogOutput.selectedWorkers;
        this.appService.excludedWorkers = teamDialogOutput.excludedWorkers;
        this.splitTeam = teamDialogOutput.splitTeam;

        this.vehicleDialog.openModal();
    }

    onVehiclePrevious() {
        this.teamDialog.openModal();
    }

    onVehicleNext(selectedVehicles: ISelectedVehicle[]) {
        this.appService.selectedVehicles = selectedVehicles;
        this.saveConfiguration();
    }

    //region Save configuration

    async saveConfiguration() {
        this.loaderService.show();
        if (!this.appService.selectedWorker?.id) {
            this.appService.clearState();
            this.router.navigate(["/setup"]);
            return;
        }

        const plannedEvent = this.appService.plannedEvent;
        if (!plannedEvent) {
            this.appService.clearState();
            this.router.navigate(["/setup"]);
            return;
        }

        const selectedWorkers = this.appService.selectedWorkers;

        const plannedEventUpdater = {
            id: plannedEvent.id,
            eventDate: plannedEvent.eventDate,
            order: plannedEvent.order,
            isNightShift: plannedEvent.isNightShift,
            isWeekendShift: plannedEvent.isWeekendShift,
            plannedWorkers: [],
            plannedVehicles: [],
        } as IPlannedEvent;

        for (let i = 0; i < selectedWorkers.length; i++) {
            const existing = plannedEvent.plannedWorkers.find(
                (x) => x.plannedEventId === plannedEvent.id && x.workerId === selectedWorkers[i].id,
            );

            const plannedWorkCreator = {
                id: existing?.id,
                workerId: selectedWorkers[i].id,
                plannedEventId: plannedEvent.id,
                displayOrder: i,
                isCreatedByMobileApp: existing?.isCreatedByMobileApp ?? true,
                date: plannedEvent.eventDate,
            } as IPlannedWorker;

            plannedEventUpdater.plannedWorkers.push(plannedWorkCreator);
        }

        this.appService.selectedVehicles.forEach((vehicle) => {
            const plannedVehicleCreator = {
                deviceId: vehicle.deviceId,
                plannedEventId: plannedEvent.id,
            } as IPlannedVehicle;

            plannedEventUpdater.plannedVehicles.push(plannedVehicleCreator);
        });

        const plannedEventUpdaterClass = new PlannedEventUpdater(plannedEventUpdater);

        try {
            const promise1 = firstValueFrom(this.plannedEventApi.update$(plannedEventUpdaterClass));

            let promise2: Promise<IPlannedEvent | void>;
            if (this.splitTeam) {
                const plannedWorkers: IPlannedWorker[] = [];
                this.appService.excludedWorkers.forEach((worker, index) => {
                    const plannedWorker = {
                        displayOrder: index,
                        isCreatedByMobileApp: true,
                        plannedEventId: null,
                        workerId: worker.id,
                    } as IPlannedWorker;

                    plannedWorkers.push(plannedWorker);
                });

                const plannedAssignments: IPlannedAssignment[] = [];
                this.appService.plannedEvent.plannedAssignments.forEach((assignment, index) => {
                    const plannedAssignment = {
                        id: null,
                        note: "",
                        safetyQuestionsChecked: false,
                        displayOrder: index,
                        checkInAtWorkChecked: null,
                        checkOutAtWorkChecked: null,
                        plannedEventId: null,
                        assignmentId: assignment.assignmentId,
                    } as IPlannedAssignment;

                    plannedAssignments.push(plannedAssignment);
                });

                const plannedEvent = {
                    eventDate: this.appService.plannedEvent.eventDate,
                    order: this.appService.plannedEvent.order + 1,
                    isNightShift: this.appService.plannedEvent.isNightShift,
                    isWeekendShift: this.appService.plannedEvent.isWeekendShift,
                    plannedWorkers: plannedWorkers,
                    plannedAssignments: plannedAssignments,
                } as IPlannedEvent;

                const plannedEventCreator = new PlannedEventCreator(plannedEvent);
                promise2 = firstValueFrom(this.plannedEventApi.create$(plannedEventCreator));
            } else {
                promise2 = Promise.resolve();
            }

            await Promise.all([promise1, promise2]).then((_results) => {
                this.appService.config = {
                    isConfigured: true,
                    language: this.appService.config.language,
                    plannedEventId: plannedEvent.id,
                };
            });

            const serviceRequestOptions = new ServiceRequestOptions();
            serviceRequestOptions.includes.add("PlannedEvent", "PlannedWorkers");
            serviceRequestOptions.includes.add("PlannedEvent", "PlannedAssignments");
            serviceRequestOptions.includes.add("PlannedEvent", "PlannedVehicles");
            serviceRequestOptions.includes.add("PlannedWorker", "Worker");
            serviceRequestOptions.includes.add("Project", "organization");

            this.appService.plannedEvent = await firstValueFrom(
                this.plannedEventApi.get$(plannedEvent.id, null, serviceRequestOptions),
            );
        } catch (error) {
            alert("An error occurred while saving the configuration");
            this.authenticationService.signoutSilently();
            console.error(error);
        } finally {
            this.loaderService.hide();
        }

        this.appService.saveState();
        this.router.navigate(["/assignments"]);
    }

    //endregion
}
